import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { PROGRAMS } from "../data/siteData";

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-brand-cream"
    >
      <div className="container-app">
        <div className="max-w-3xl mb-16">
          <p className="overline mb-4">Three Programs · One Mission</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-brand-forest tracking-tight">
            Whatever your situation, there is a path forward.
          </h2>
          <p className="mt-6 text-lg text-brand-muted leading-relaxed">
            From the first late notice to the day you hand over the keys —
            choose the program that fits where you are right now.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
          {/* Pre-foreclosure (large) */}
          <ServiceCard program={PROGRAMS[0]} className="lg:col-span-7 lg:row-span-2" tall />
          {/* Short sale */}
          <ServiceCard program={PROGRAMS[1]} className="lg:col-span-5" gold />
          {/* Cash buyer */}
          <ServiceCard program={PROGRAMS[2]} className="lg:col-span-5" />
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ program, className = "", tall = false, gold = false }) => {
  const isGold = gold;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6 }}
      data-testid={`service-card-${program.id}`}
      className={`group relative rounded-md border overflow-hidden transition-all duration-500 hover:-translate-y-1 ${
        isGold
          ? "bg-brand-gold border-brand-gold-deep"
          : "bg-brand-forest border-brand-forest-deep text-brand-cream"
      } ${className}`}
    >
      <div
        className={`relative p-8 md:p-10 flex flex-col h-full ${
          tall ? "min-h-[440px]" : "min-h-[340px]"
        }`}
      >
        <div className="flex items-center justify-between">
          <span
            className={`text-xs uppercase tracking-[0.22em] font-semibold ${
              isGold ? "text-brand-forest" : "text-brand-gold"
            }`}
          >
            {program.short}
          </span>
          <ArrowUpRight
            className={`h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 ${
              isGold ? "text-brand-forest" : "text-brand-cream"
            }`}
          />
        </div>

        <h3
          className={`mt-6 font-heading text-2xl md:text-3xl font-bold leading-tight ${
            isGold ? "text-brand-forest" : "text-brand-cream"
          }`}
        >
          {program.label}
        </h3>

        <p
          className={`mt-4 text-base leading-relaxed ${
            isGold ? "text-brand-forest/80" : "text-brand-cream/80"
          }`}
        >
          {program.blurb}
        </p>

        <ul className="mt-6 space-y-2.5 flex-1">
          {program.bullets.map((b, i) => (
            <li
              key={i}
              className={`flex items-start gap-2.5 text-sm ${
                isGold ? "text-brand-forest/90" : "text-brand-cream/90"
              }`}
            >
              <Check
                className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                  isGold ? "text-brand-forest" : "text-brand-gold"
                }`}
              />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        <a
          href="#lead-form"
          data-testid={`service-cta-${program.id}`}
          className={`mt-8 inline-flex items-center gap-2 text-sm font-semibold border-b pb-1 self-start transition-all ${
            isGold
              ? "text-brand-forest border-brand-forest hover:gap-3"
              : "text-brand-gold border-brand-gold hover:gap-3"
          }`}
        >
          {program.cta}
          <ArrowUpRight className="h-4 w-4" />
        </a>
      </div>
    </motion.div>
  );
};
